import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Space, Spin } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  DownloadOutlined,
  ColumnWidthOutlined
} from '@ant-design/icons';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker - use local copy from public directory
// This ensures CSP compliance and version compatibility
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

/**
 * PDFViewerCore - Core PDF viewer component without container
 *
 * This is a reusable PDF viewer that can be embedded in modals, drawers, or any container.
 * It provides zoom, navigation, and download controls.
 *
 * @param {string} pdfUrl - URL of the PDF file to display
 * @param {string} filename - Original filename for downloads
 * @param {boolean} showToolbar - Whether to show the toolbar (default: true)
 * @param {string} toolbarPosition - 'top' or 'bottom' (default: 'top')
 * @param {function} onDownload - Custom download handler (optional)
 * @param {object} containerStyle - Custom styles for the container
 */
const PDFViewerCore = ({
  pdfUrl,
  filename = 'document.pdf',
  showToolbar = true,
  toolbarPosition = 'top',
  onDownload,
  containerStyle = {}
}) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [pageWidth, setPageWidth] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);

  // Fetch PDF with authentication if needed (for local files)
  useEffect(() => {
    const fetchPDF = async () => {
      if (!pdfUrl) return;

      // Check if URL requires authentication (local files - archive or admin content)
      if (pdfUrl.includes('/archive/local-file/') || pdfUrl.includes('/admin/content/')) {
        try {
          const user = JSON.parse(localStorage.getItem('user'));
          const response = await fetch(pdfUrl, {
            headers: {
              'Authorization': `Bearer ${user?.access_token}`
            }
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch PDF: ${response.statusText}`);
          }

          const blob = await response.blob();
          setPdfFile(blob);
          setLoading(false);
        } catch (error) {
          console.error('Error fetching PDF:', error);
          setLoading(false);
        }
      } else {
        // Public URL, use directly
        setPdfFile(pdfUrl);
        setLoading(false);
      }
    };

    fetchPDF();
  }, [pdfUrl]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
    // Set initial width to fit container
    if (containerRef.current) {
      setPageWidth(containerRef.current.offsetWidth - 40);
    }
  };

  // Update page width on container resize
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      if (pageWidth !== null && containerRef.current) {
        setPageWidth(containerRef.current.offsetWidth - 40);
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [pageWidth]);

  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages));
  };

  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 3.0));
    setPageWidth(null); // Clear fixed width when zooming
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
    setPageWidth(null); // Clear fixed width when zooming
  };

  const fitToWidth = () => {
    if (containerRef.current) {
      setPageWidth(containerRef.current.offsetWidth - 40);
      setScale(1.0);
    }
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload(pdfUrl, filename);
    } else {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const toolbar = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: toolbarPosition === 'top' ? '1px solid #e8e8e8' : 'none',
        borderTop: toolbarPosition === 'bottom' ? '1px solid #e8e8e8' : 'none',
        backgroundColor: 'var(--rlc-surface-subtle)'
      }}
    >
      <Space size="large">
        {/* Navigation Controls */}
        <Space>
          <Button
            icon={<LeftOutlined />}
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
            title="Previous page"
          />
          <span style={{ minWidth: '100px', textAlign: 'center' }}>
            Page {pageNumber} / {numPages || '...'}
          </span>
          <Button
            icon={<RightOutlined />}
            onClick={goToNextPage}
            disabled={pageNumber >= numPages}
            title="Next page"
          />
        </Space>

        {/* Zoom Controls */}
        <Space>
          <Button
            icon={<ZoomOutOutlined />}
            onClick={zoomOut}
            disabled={scale <= 0.5}
            title="Zoom out"
          />
          <span style={{ minWidth: '60px', textAlign: 'center' }}>
            {Math.round(scale * 100)}%
          </span>
          <Button
            icon={<ZoomInOutlined />}
            onClick={zoomIn}
            disabled={scale >= 3.0}
            title="Zoom in"
          />
          <Button
            icon={<ColumnWidthOutlined />}
            onClick={fitToWidth}
            title="Fit to width"
          />
        </Space>
      </Space>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', ...containerStyle }}>
      {/* Toolbar at top */}
      {showToolbar && toolbarPosition === 'top' && toolbar}

      {/* PDF Display Area */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          overflow: 'auto',
          textAlign: 'center',
          backgroundColor: '#525659',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          padding: '20px'
        }}
      >
        {loading ? (
          <div style={{ padding: '40px', color: 'white' }}>
            <Spin size="large" tip="Fetching PDF..." />
          </div>
        ) : pdfFile ? (
          <Document
            file={pdfFile}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <div style={{ padding: '40px', color: 'white' }}>
                <Spin size="large" tip="Loading PDF..." />
              </div>
            }
            error={
              <div style={{ padding: '20px', color: 'var(--rlc-state-error)', backgroundColor: 'white', borderRadius: '4px' }}>
                Failed to load PDF. Please try again or{' '}
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                  download the file
                </a>
                .
              </div>
            }
          >
            <Page
              pageNumber={pageNumber}
              renderTextLayer={true}
              renderAnnotationLayer={true}
              loading={
                <div style={{ padding: '20px', color: 'white' }}>
                  <Spin tip="Loading page..." />
                </div>
              }
              width={pageWidth}
              scale={pageWidth ? undefined : scale}
              className="pdf-page-canvas"
            />
          </Document>
        ) : (
          <div style={{ padding: '20px', color: 'var(--rlc-state-error)', backgroundColor: 'white', borderRadius: '4px' }}>
            Failed to fetch PDF file.
          </div>
        )}
      </div>

      {/* Toolbar at bottom */}
      {showToolbar && toolbarPosition === 'bottom' && toolbar}
    </div>
  );
};

PDFViewerCore.propTypes = {
  pdfUrl: PropTypes.string.isRequired,
  filename: PropTypes.string,
  showToolbar: PropTypes.bool,
  toolbarPosition: PropTypes.oneOf(['top', 'bottom']),
  onDownload: PropTypes.func,
  containerStyle: PropTypes.object
};

export default PDFViewerCore;
